import React, { useCallback, useRef, useState } from "react";
import SendWhatsAppConfirmModal, {
  digitsOnlyPhone,
} from "../components/SendWhatsAppConfirmModal";
import { sendThankYouWhatsApp } from "../apis/visit";
import { getSettings } from "../apis/setting";
import { getPatient } from "../apis/patient";
import { useNotification } from "../NotificationContext";
import { isSendThankYouMessageEnabled } from "../utils/ClinicMessaging";

type PromptArgs = {
  visitId: number;
  patientId?: number | null;
  patientPhone?: string | null;
  onDone?: () => void;
};

const checkThankYouEnabled = async (): Promise<boolean> => {
  try {
    const res: any = await getSettings();
    const settings = res?.data?.data ?? [];
    const item = settings.find(
      (s: any) => s.code === "send_thank_you_message"
    );
    if (item) return item.value === "yes";
  } catch {
    // fall through
  }
  return isSendThankYouMessageEnabled();
};

export const useThankYouWhatsAppPrompt = () => {
  const { showNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [visitId, setVisitId] = useState<number | null>(null);
  const [initialPhone, setInitialPhone] = useState("");
  const [sending, setSending] = useState(false);
  const onDoneRef = useRef<(() => void) | null>(null);

  const closeAndFinish = useCallback(() => {
    setOpen(false);
    setVisitId(null);
    setInitialPhone("");
    const done = onDoneRef.current;
    onDoneRef.current = null;
    done?.();
  }, []);

  const promptIfEnabled = useCallback(async (args: PromptArgs) => {
    onDoneRef.current = args.onDone ?? null;

    const enabled = await checkThankYouEnabled();
    if (!enabled) {
      args.onDone?.();
      onDoneRef.current = null;
      return;
    }

    let phone = digitsOnlyPhone(String(args.patientPhone ?? ""));
    if (phone.length !== 10 && args.patientId) {
      try {
        const patientRes: any = await getPatient(args.patientId);
        const patient =
          patientRes?.data?.data ?? patientRes?.data ?? patientRes;
        phone = digitsOnlyPhone(String(patient?.phone ?? ""));
      } catch {
        // user can type the number
      }
    }

    setVisitId(args.visitId);
    setInitialPhone(phone);
    setOpen(true);
  }, []);

  const handleConfirm = useCallback(
    async (phone: string) => {
      if (!visitId) {
        closeAndFinish();
        return;
      }
      setSending(true);
      try {
        await sendThankYouWhatsApp(visitId, phone);
        showNotification(
          "success",
          "Thank-you WhatsApp sent to patient",
          "",
          "topRight"
        );
        closeAndFinish();
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          "Failed to send thank-you WhatsApp";
        showNotification("error", msg, "", "topRight");
      } finally {
        setSending(false);
      }
    },
    [visitId, closeAndFinish, showNotification]
  );

  const ThankYouWhatsAppModal = (
    <SendWhatsAppConfirmModal
      open={open}
      title="Send thank-you WhatsApp?"
      description="Visit completed. Confirm the patient's WhatsApp number below, or change it before sending."
      initialPhone={initialPhone}
      sending={sending}
      confirmLabel="Yes, send WhatsApp"
      cancelLabel="No"
      onConfirm={handleConfirm}
      onCancel={() => {
        if (!sending) closeAndFinish();
      }}
    />
  );

  return { promptIfEnabled, ThankYouWhatsAppModal };
};
